import { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  routineEnum,
  routineTypeEnum,
  complianceEnum,
  refillEnum,
} from "./data";
import { useSelector, useDispatch } from "react-redux";
import { updateMedicationReview } from "../../../../../redux/modules/clinical-services/actions";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import { useParams } from "react-router-dom";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import SubNavigation from "../../common/sub-navigation";
import { scrollX } from "../.././../../../helpers/functions";
import { updatePatientPersonalDetails } from "../../../../../redux/modules/patient/actions";
import { displayToast } from "../../../../../redux/modules/toast/actions";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";

const Adherence = () => {
  //State Variables
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } =
    useSelector((state) => state.clinicalService);
  const { patient } = useSelector((state) => state.patients);
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const { id: patientId, serviceId } = useParams();
  const dispatch = useDispatch();

  //Local Variables
  const [routineVal, setRoutineVal] = useState(null);
  const [routineTypeVal, setRoutineTypeVal] = useState(null);
  const [complianceVal, setComplianceVal] = useState(null);
  const [refillVal, setRefillVal] = useState(null);
  const [showAnalysisCard, setShowAnalysisCard] = useState(false);
  const [openAdvancedRisk, setOpenAdvancedRisk] = useState(false);
  const [openNoAdvancedRisk, setOpenNoAdvancedRisk] = useState(false);
  const [isAdherence, setIsAdherence] = useState(false);
  const [clicked, setClicked] = useState(false);

  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [clinicalAnalysis, setClinicalAnalysis] = useState(false);

  //state management
  useEffect(() => {
    let obj =
      clinicalServiceName === ClinicalServiceTypes.MedicationReview &&
      clinicalServiceObj.initialAssessment.initialAssessmentForm
        .clinicalAssessment;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      obj !== null &&
      obj !== undefined &&
      obj.routine !== null
    ) {
      setComplianceVal(obj.compliance ? "Yes" : "No");
      setRefillVal(obj?.refill);
      setRoutineVal(obj?.routine);
      setRoutineTypeVal(obj?.routineType);
      setClinicalAnalysis(true);
      setCompleteSubStep(true);
      setShowAnalysisCard(true);
      scrollX("adherence-ui", 480 * 5);
      setClicked(false);
    }
  }, [clinicalServiceObj]);

  useEffect(() => {
    if (clicked) {
      if (
        routineVal &&
        (routineVal === "No" || routineTypeVal) &&
        complianceVal &&
        refillVal
      )
        setShowAnalysisCard(true);
      else setShowAnalysisCard(false);

      setClinicalAnalysis(false);
      setCompleteSubStep(false);
    }
  }, [routineVal, routineTypeVal, complianceVal, refillVal]);

  //Move Back and Forth
  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(
        clinicalServiceSteps,
        currentStep,
        currentSubStep,
        1
      );

      if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
        const updatedClinicalServiceObj = {
          clinicalAssessment: {
            compliance: complianceVal === "Yes" ? true : false,
            refill: refillVal,
            routine: routineVal,
            routineType: routineTypeVal,
          },
          currentStep: nextStep,
          currentSubStep: nextSubStep,
          serviceSteps: clinicalServiceSteps,
        };
        dispatch(updateMedicationReview(serviceId, updatedClinicalServiceObj));
        updatePatient();
      }
    }
  };

  const updatePatient = () => {
    const transformedObject = {
      firstName: patient.firstName,
      middleName: patient.middleName,
      lastName: patient.lastName,
      dateOfBirth: patient.dateOfBirth,
      biologicalSex: patient.biologicalSex,
      gender: patient.gender,
      status: patient.status,
      payingCash: patient.payingCash,
      colorBlind: patient.colorBlind,
      primaryLanguage: patient.primaryLanguage,
      preferences: patient.preferences,
      characteristics: patient.characteristics,
      medicationAdministration: patient.medicationAdministration,
      isMedicationAdherence: isAdherence,
    };
    let statusObject = {
      statusChanged: false,
      oldStatus: patient.status,
      newStatus: patient.status,
      onlyStatusChanged: false,
    };

    dispatch(
      updatePatientPersonalDetails(patient.id, transformedObject, statusObject, { showToast: false })
    );
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(
      clinicalServiceSteps,
      currentStep,
      currentSubStep,
      -1
    );
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };

    if (clinicalServiceName === ClinicalServiceTypes.MedicationReview) {
      dispatch(updateMedicationReview(serviceId, updatedSteps));
    }
  };

  const handleAlerts = () => {
    if (
      (routineVal !== "No" && routineTypeVal === "Usually") ||
      complianceVal === "Yes" ||
      refillVal === "Late"
    ) {
      setIsAdherence(true);
      setOpenAdvancedRisk(true);
    } else {
      setIsAdherence(false);
      setOpenNoAdvancedRisk(true);
    }
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Medication Adherence
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="adherence-ui"
              >
                {/* Medication Routine */}
                <div>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Medication Routine{" "}
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {routineEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {routineEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  if (option.value === "No")
                                    setRoutineTypeVal(null);
                                  setRoutineVal(option.value);
                                  setClicked(true);
                                  setTimeout(() => {
                                    scrollX("adherence-ui", 480);
                                  }, 100);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      routineVal === option.value
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                        {routineVal != null &&
                          routineVal.length > 0 &&
                          routineVal != "No" && (
                            <>
                              <div className="border-b border-gray-100"></div>
                              <p className="text-lg h-[31px] font-semibold text-gray-800">
                                {routineTypeEnum.question}
                              </p>
                              <div className="flex flex-col gap-2">
                                {routineTypeEnum.options.map((option) => {
                                  return (
                                    <button
                                      onClick={() => {
                                        setRoutineTypeVal(option.value);
                                        setClicked(true);
                                        setTimeout(() => {
                                          scrollX("adherence-ui", 480);
                                        }, 100);
                                      }}
                                      className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      routineTypeVal === option.value
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                                    >
                                      <p className="font-semibold text-body-md flex justify-start items-start">
                                        {option.header}
                                      </p>
                                      <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                        {option.subHeader}
                                      </p>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Medication Compliance */}
                <div>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Medication Compliance
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] font-semibold text-gray-800">
                          {complianceEnum.question}
                        </p>
                        <div className="flex flex-col gap-2">
                          {complianceEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setComplianceVal(option.value);
                                  setClicked(true);
                                  setTimeout(() => {
                                    scrollX("adherence-ui", 480);
                                  }, 100);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      complianceVal === option.value
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Medication Refill Habits */}
                <div>
                  <div className="h-[595px] rounded-[4px] overflow-hidden border border-gray-100 flex flex-col">
                    <div
                      className="items-center justify-start flex flex-row px-5 py-4 bg-cover bg-center"
                      style={{
                        backgroundImage: `url(/icons/texture-background-2.png)`,
                      }}
                    >
                      <h3 className="text-xl font-bold text-teal-600 ">
                        Medication Refill Habits
                      </h3>
                    </div>
                    <div className="flex flex-col gap-5 p-5 overflow-scroll overflow-x-hidden bg-scroll min-h-[465px]">
                      <div className="flex flex-col gap-4">
                        <p className="text-lg h-[51px] text-gray-800">
                          <span className="font-semibold">
                            {refillEnum.question}
                          </span>
                        </p>
                        <div className="flex flex-col gap-2">
                          {refillEnum.options.map((option) => {
                            return (
                              <button
                                onClick={() => {
                                  setRefillVal(option.value);
                                  setClicked(true);
                                  setTimeout(() => {
                                    scrollX("adherence-ui", 480);
                                  }, 100);
                                }}
                                className={`"font-semibold text-body-md p-3 rounded-[4px] border-[1px]
                    text-gray-800 hover:text-gray-800 hover:bg-gray-20 hover:border-gray-600" ${
                      refillVal === option.value
                        ? "bg-gray-20 border-gray-600"
                        : "bg-white border-gray-100"
                    }`}
                              >
                                <p className="font-semibold text-body-md flex justify-start items-start">
                                  {option.header}
                                </p>
                                <p className="font-semibold text-gray-600 text-body-md flex justify-start items-start">
                                  {option.subHeader}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Clinical Analysis */}
                {showAnalysisCard && (
                  <div className="relative">
                    <div className="h-[585px] rounded-[4px] flex flex-col overflow-hidden border border-gray-100">
                      <div className="h-[420px] w-[420px] rounded-full flex items-center justify-center relative m-8 bg-blue-100 bg-opacity-40">
                        <ReactSVG
                          src={"/icons/adherence-clock.svg"}
                          className="absolute top-[20%] left-[35%]"
                        />
                        <ReactSVG
                          src={"/icons/adherence-med.svg"}
                          className="absolute top-[45%] left-[20%]"
                        />
                      </div>
                      <div className="mx-7 my-5">
                        {!clinicalAnalysis && (
                          <Button
                            className="w-full !px-2 !text-xl"
                            type="primary"
                            text="Assess Adherence Risk"
                            onClick={() => {
                              scrollX("adherence-ui");
                              handleAlerts();
                            }}
                          />
                        )}
                        {clinicalAnalysis && (
                          <h4 className="text-xl text-REELTeal font-bold items-center justify-center flex">
                            Adherence Assessment Complete
                          </h4>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4  rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px]  ${
              !completeSubStep && "opacity-40 pointer-events-none"
            }`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
      <ConfirmationModalNew
        open={openAdvancedRisk}
        title={`Medication Adherence`}
        content={() => (
          <>
            <div className={`w-[387px] flex flex-col gap-4 text-gray-600`}>
              <p className="flex items-center text-base font-semibold ">
                Potential risk for medication non-adherence identified
              </p>
            </div>
          </>
        )}
        confirmText={"Continue"}
        cancelText="Cancel"
        onCross={() => {
          setOpenAdvancedRisk(false);
        }}
        handleClose={() => {
          setOpenAdvancedRisk(false);
        }}
        confirmAction={() => {
          setOpenAdvancedRisk(false);
          setCompleteSubStep(true);
          setClinicalAnalysis(true);
          dispatch(displayToast("Personal information has been updated.", "success", { autoClose: 4000 }));
          // Update patient record (so adherence icon updates) but do NOT advance to next step
          updatePatient();
        }}
      />
      <ConfirmationModalNew
        open={openNoAdvancedRisk}
        title={`Medication Adherence`}
        content={() => (
          <>
            <div className={`w-[387px] flex flex-col gap-4 text-gray-600`}>
              <p className="flex items-center text-base font-semibold ">
                Low risk for medication non-adherence.
              </p>
            </div>
          </>
        )}
        confirmText={"Continue"}
        cancelText="Cancel"
        onCross={() => {
          setOpenNoAdvancedRisk(false);
        }}
        handleClose={() => {
          setOpenNoAdvancedRisk(false);
        }}
        confirmAction={() => {
          setOpenNoAdvancedRisk(false);
          setCompleteSubStep(true);
          setClinicalAnalysis(true);
          dispatch(displayToast("Personal information has been updated.", "success", { autoClose: 4000 }));
          // Update patient record (so adherence icon updates) but do NOT advance to next step
          updatePatient();
        }}
      />
    </div>
  );
};

export default Adherence;
